import { SleuthMark } from "../components/SleuthMark";

export function Landing() {
  return (
    <>
      <Hero />
      <FeatureGrid />
      <PrivacyTease />
    </>
  );
}

function Hero() {
  return (
    <section className="mx-auto max-w-5xl px-6 pt-20 pb-16">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-10 items-center">
        <div>
          <p className="eyebrow mb-5">On the case while you play</p>
          <h1 className="display mb-5">Is it me, or is it the game?</h1>
          <p className="lede max-w-xl mb-8 text-[var(--color-text-muted)]">
            LagSleuth lives in the menubar, watches your network during sessions, and
            gives you a single answer when your shots stop landing. Your Wi-Fi, your
            ISP, or the game's servers — Sleuth tells you which.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <DownloadButton platform="mac" />
            <DownloadButton platform="win" />
          </div>
          <p className="mt-5 text-xs text-[var(--color-text-dim)]">
            macOS 11 or later · Windows coming after launch · No account, no telemetry
          </p>
        </div>
        <div className="hidden md:block">
          <SleuthMark className="w-44 h-44" />
        </div>
      </div>
    </section>
  );
}

type Platform = "mac" | "win";

function DownloadButton({ platform }: { platform: Platform }) {
  const isMac = platform === "mac";
  const enabled = false; // flip when App Store / installer URLs exist
  const label = isMac ? "Download for macOS" : "Windows · soon";
  const sublabel = isMac ? "Universal · Apple silicon + Intel" : "Notarized build in beta";

  return (
    <button
      type="button"
      disabled={!enabled}
      className={`inline-flex items-center gap-3 px-5 py-3 rounded-md border transition-colors ${
        enabled
          ? "bg-[var(--color-accent-soft)] border-[var(--color-accent-line)] hover:bg-[var(--color-accent-soft)] hover:border-[var(--color-accent)] text-[var(--color-text-strong)]"
          : "bg-[var(--color-surface)] border-[var(--color-border-soft)] text-[var(--color-text-muted)] cursor-not-allowed"
      }`}
      title={enabled ? undefined : "Available once the App Store listing goes live."}
    >
      <PlatformGlyph platform={platform} />
      <span className="flex flex-col items-start leading-tight">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-[11px] text-[var(--color-text-dim)]">{sublabel}</span>
      </span>
    </button>
  );
}

function PlatformGlyph({ platform }: { platform: Platform }) {
  if (platform === "mac") {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M16.5 12.5c0-2.5 2-3.7 2.1-3.8-1.2-1.7-3-1.9-3.6-2-1.5-.2-3 .9-3.7.9-.8 0-2-.9-3.3-.9-1.7 0-3.2 1-4.1 2.5-1.7 3-.4 7.4 1.3 9.8.8 1.2 1.8 2.5 3 2.5 1.2 0 1.7-.8 3.1-.8 1.5 0 1.9.8 3.2.8 1.3 0 2.2-1.2 3-2.4.9-1.4 1.3-2.8 1.3-2.8s-2.3-.9-2.3-3.8zM14 5.5c.7-.8 1.1-2 1-3.1-1 0-2.2.7-2.9 1.5-.6.7-1.2 1.9-1 3 1.1.1 2.3-.6 2.9-1.4z" />
      </svg>
    );
  }
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M3 5.4 11 4.3v7.4H3V5.4zm0 7.7h8v7.4L3 19.4v-6.3zm9-8.8L21.5 3v8.7H12V4.3zM12 12.7h9.5V21L12 19.7v-7z" />
    </svg>
  );
}

function FeatureGrid() {
  return (
    <section className="mx-auto max-w-5xl px-6 py-10">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <Feature
          eyebrow="Quiet by default"
          title="Watches without interrupting"
          body="LagSleuth lives in the menubar as a small color dot. No tray badges, no popups during play. A notification only fires when something is actually wrong, and it's worded for humans."
        />
        <Feature
          eyebrow="Knows the difference"
          title="Local vs. ISP vs. server"
          body="Per-session probes to baseline endpoints, your router, and the game's server let Sleuth tell you whether the spike was on your Wi-Fi, somewhere in your ISP, or upstream at the publisher."
        />
        <Feature
          eyebrow="Yours, privately"
          title="Nothing transmitted about you"
          body="No account. No analytics. No crash reports. Sleuth fetches a public list of game-server endpoints once per day; that's the only outbound request it makes that isn't probing for your own session."
        />
      </div>
    </section>
  );
}

function Feature({
  eyebrow,
  title,
  body,
}: {
  eyebrow: string;
  title: string;
  body: string;
}) {
  return (
    <article className="rounded-xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] p-6">
      <p className="eyebrow mb-3">{eyebrow}</p>
      <h3 className="h2 mb-2.5">{title}</h3>
      <p className="text-sm text-[var(--color-text-muted)] leading-relaxed">{body}</p>
    </article>
  );
}

function PrivacyTease() {
  return (
    <section className="mx-auto max-w-3xl px-6 py-16 text-center">
      <p className="eyebrow mb-3">The short version</p>
      <p className="text-lg text-[var(--color-text)] leading-relaxed">
        LagSleuth does not transmit data about you, your network, or your activity.
        The app fetches a public list of game-server endpoints once per day from{" "}
        <span className="num text-[var(--color-text-strong)]">
          presets.lagsleuth.latentlab.cc
        </span>
        ; that request carries no identifying information.
      </p>
      <p className="mt-5 text-sm">
        <a href="/privacy">Read the full privacy policy →</a>
      </p>
    </section>
  );
}
